import { FuelType, FuelSpec, FuelDuration } from "../types/fuel";

// Fuel types and specifications
export const FUEL_TYPES: Record<FuelType, FuelSpec> = {
  REACTION: {
    type: "REACTION",
    name: "Reaction Drive Fuel",
    description: "Standard fuel for reaction drives",
    costPerTon: 1000, // Cr1,000 per ton
    techLevel: 7,
    storageRequirements: {
      minTons: 1,
      maxTons: Infinity,
      special: null,
    },
    consumptionRate: {
      baseRate: 0.025, // 2.5% per thrust per hour
      thrust0Rate: 0.25, // 0.25 tons per hour for thrust 0
      combatRoundsPerHour: 10,
    },
  },
  JUMP: {
    type: "JUMP",
    name: "Jump Drive Fuel",
    description: "Specialized fuel for jump drives",
    costPerTon: 2000, // Cr2,000 per ton
    techLevel: 7,
    storageRequirements: {
      minTons: 1,
      maxTons: Infinity,
      special: "Must be stored in jump drive fuel tanks",
    },
    consumptionRate: {
      baseRate: 0.1, // 10% of hull tonnage per jump rating
      thrust0Rate: 0,
      combatRoundsPerHour: 0,
    },
  },
  POWER_PLANT: {
    type: "POWER_PLANT",
    name: "Power Plant Fuel",
    description: "Fuel for power plants",
    costPerTon: 500, // Cr500 per ton
    techLevel: 7,
    storageRequirements: {
      minTons: 1,
      maxTons: Infinity,
      special: null,
    },
    consumptionRate: {
      baseRate: 0.1, // 10% of power plant size per month
      thrust0Rate: 0,
      combatRoundsPerHour: 0,
    },
  },
} as const;

// Fuel duration options
export const FUEL_DURATIONS = [
  { label: "1 Week", value: 1, weeks: 1 },
  { label: "2 Weeks", value: 2, weeks: 2 },
  { label: "1 Month", value: 4, weeks: 4 },
  { label: "2 Months", value: 8, weeks: 8 },
  { label: "3 Months", value: 12, weeks: 12 },
  { label: "6 Months", value: 24, weeks: 24 },
  { label: "1 Year", value: 52, weeks: 52 },
] as const;

// Type assertion for the array
export const FUEL_DURATIONS_ARRAY: FuelDuration[] =
  FUEL_DURATIONS as unknown as FuelDuration[];

// Fuel duration options (renamed for backward compatibility)
export const FUEL_DURATION_OPTIONS = FUEL_DURATIONS;

// Fuel requirements
export const FUEL_REQUIREMENTS = {
  REACTION: {
    baseRate: 0.025, // 2.5% per thrust per hour
    thrust0Rate: 0.25, // 0.25 tons per hour for thrust 0
    combatRoundsPerHour: 10,
  },
  JUMP: {
    baseRate: 0.1, // 10% of hull tonnage per jump rating
    thrust0Rate: 0,
    combatRoundsPerHour: 0,
  },
  POWER_PLANT: {
    baseRate: 0.1, // 10% of power plant size per month
    thrust0Rate: 0,
    combatRoundsPerHour: 0,
  },
} as const;

// Helper functions
export const getFuelSpec = (type: FuelType): FuelSpec => {
  return FUEL_TYPES[type];
};

export const calculateFuelCost = (type: FuelType, tons: number): number => {
  const spec = getFuelSpec(type);
  return spec.costPerTon * tons;
};

export const calculateReactionFuelConsumption = (
  thrust: number,
  hours: number,
  shipTons: number,
): number => {
  const spec = getFuelSpec("REACTION");
  if (thrust === 0) {
    if (!spec.consumptionRate.thrust0Rate) {
      throw new Error(
        "Thrust 0 consumption rate is required for reaction drives",
      );
    }
    return spec.consumptionRate.thrust0Rate * hours;
  }
  return shipTons * spec.consumptionRate.baseRate * thrust * hours;
};

export const calculateJumpFuelConsumption = (
  jumpRating: number,
  shipTons: number,
): number => {
  const spec = getFuelSpec("JUMP");
  return shipTons * spec.consumptionRate.baseRate * jumpRating;
};

export const calculatePowerPlantFuelConsumption = (
  powerPlantTons: number,
  weeks: number,
): number => {
  const spec = getFuelSpec("POWER_PLANT");
  const monthlyRate = spec.consumptionRate.baseRate;
  const weeklyRate = monthlyRate / 4;
  return Math.max(1, powerPlantTons * weeklyRate * weeks);
};

// Validation functions
export const validateFuelSpec = (spec: FuelSpec, weightOverrideActive: boolean = false): string[] => {
  const errors: string[] = [];

  if (!spec.type) {
    errors.push("Fuel type is required");
  }

  if (!spec.name) {
    errors.push("Fuel name is required");
  }

  if (spec.costPerTon < 0) {
    errors.push("Cost per ton cannot be negative");
  }

  if (spec.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (!weightOverrideActive) {
    if (spec.storageRequirements.minTons < 0) {
      errors.push("Minimum storage tons cannot be negative");
    }
    if (spec.storageRequirements.maxTons <= spec.storageRequirements.minTons) {
      errors.push(
        "Maximum storage tons must be greater than minimum storage tons",
      );
    }
  }

  if (spec.consumptionRate.baseRate < 0) {
    errors.push("Base consumption rate cannot be negative");
  }

  // Only validate thrust0Rate for reaction drives
  if (spec.type === "REACTION") {
    if (!spec.consumptionRate.thrust0Rate) {
      errors.push("Thrust 0 consumption rate is required for reaction drives");
    } else if (spec.consumptionRate.thrust0Rate < 0) {
      errors.push("Thrust 0 consumption rate cannot be negative");
    }

    if (!spec.consumptionRate.combatRoundsPerHour) {
      errors.push("Combat rounds per hour is required for reaction drives");
    } else if (spec.consumptionRate.combatRoundsPerHour < 0) {
      errors.push("Combat rounds per hour cannot be negative");
    }
  }

  return errors;
};

export const validateFuelStorage = (
  type: FuelType,
  tons: number,
  shipTons: number,
  weightOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];
  const spec = getFuelSpec(type);

  if (!weightOverrideActive) {
    if (tons < spec.storageRequirements.minTons) {
      errors.push(
        `Fuel storage must be at least ${spec.storageRequirements.minTons} tons`,
      );
    }
    if (tons > spec.storageRequirements.maxTons) {
      errors.push(
        `Fuel storage cannot exceed ${spec.storageRequirements.maxTons} tons`,
      );
    }
    if (type === "JUMP" && tons < shipTons * 0.1) {
      errors.push("Jump drive fuel must be at least 10% of ship tonnage");
    }
  }

  return errors;
};
